import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

/** Onboarding is active when on "/" and not yet marked complete. */
function isOnboardingActive(pathname: string): boolean {
  return pathname === "/" && localStorage.getItem("grounded_onboarding_complete") !== "true";
}

/**
 * Priority levels for prompt dialogs.
 * Lower number = higher priority.
 */
export enum PromptPriority {
  TERMS = 0,           // Must accept before anything else
  CHECK_IN = 10,       // User is at a cafe — don't interrupt
  FOLLOWUP_VIBE = 15,  // 2.5h follow-up "still at X?" prompt
  NAME = 20,           // Profile completion
  USERNAME = 25,       // Username prompt (after name)
  LOCATION = 30,       // "Where are you based?"
  PREFERENCE = 40,     // "What matters most?"
  FEEDBACK = 50,       // Low priority prompts
  APP_REVIEW = 55,     // App Store review ask — sits below FEEDBACK so feedback wins
}

interface PromptRequest {
  id: string;
  priority: PromptPriority;
}

interface PromptQueueContextType {
  /** Request permission to show a prompt. Returns true if granted immediately. */
  requestPrompt: (id: string, priority: PromptPriority) => boolean;
  /** Release the prompt slot when dialog closes. */
  releasePrompt: (id: string) => void;
  /** Check if a specific prompt currently holds the slot. */
  isActivePrompt: (id: string) => boolean;
  /** Check if any prompt is currently showing. */
  hasActivePrompt: boolean;
}

const PromptQueueContext = createContext<PromptQueueContextType | null>(null);

export function PromptQueueProvider({ children }: { children: ReactNode }) {
  const [activePrompt, setActivePrompt] = useState<PromptRequest | null>(null);
  const queueRef = useRef<PromptRequest[]>([]);
  const prevOnboardingActiveRef = useRef(false);
  const location = useLocation();
  const onboardingActive = isOnboardingActive(location.pathname);

  const processQueue = useCallback(() => {
    if (queueRef.current.length === 0) return;
    if (isOnboardingActive(window.location.pathname)) return;
    // Sort by priority (lower = higher priority)
    queueRef.current.sort((a, b) => a.priority - b.priority);
    const next = queueRef.current.shift()!;
    setActivePrompt(next);
  }, []);

  const requestPrompt = useCallback((id: string, priority: PromptPriority): boolean => {
    // Suppress all prompt activation while onboarding flow is open.
    // Queue the request so it can fire after onboarding completes.
    if (isOnboardingActive(window.location.pathname)) {
      if (!queueRef.current.some(r => r.id === id) && activePrompt?.id !== id) {
        queueRef.current.push({ id, priority });
      }
      return false;
    }

    // If nothing active, grant immediately
    if (!activePrompt) {
      // Also check queue
      if (queueRef.current.length === 0) {
        setActivePrompt({ id, priority });
        return true;
      }
    }

    // If this prompt has higher priority than the active one, preempt
    if (activePrompt && priority < activePrompt.priority) {
      // Put active back in queue
      queueRef.current.push(activePrompt);
      setActivePrompt({ id, priority });
      return true;
    }

    // Otherwise queue it (avoid duplicates)
    if (!queueRef.current.some(r => r.id === id) && activePrompt?.id !== id) {
      queueRef.current.push({ id, priority });
    }
    return false;
  }, [activePrompt]);

  const releasePrompt = useCallback((id: string) => {
    setActivePrompt(prev => {
      if (prev?.id === id) {
        // Process queue on next tick
        setTimeout(processQueue, 2000);
        return null;
      }
      return prev;
    });
    // Also remove from queue if it was queued
    queueRef.current = queueRef.current.filter(r => r.id !== id);
  }, [processQueue]);

  const isActivePrompt = useCallback((id: string): boolean => {
    return activePrompt?.id === id;
  }, [activePrompt]);

  // When onboarding ends, drain queued prompts.
  useEffect(() => {
    if (prevOnboardingActiveRef.current && !onboardingActive && queueRef.current.length > 0) {
      processQueue();
    }
    prevOnboardingActiveRef.current = onboardingActive;
  }, [onboardingActive, processQueue]);

  return (
    <PromptQueueContext.Provider value={{
      requestPrompt,
      releasePrompt,
      isActivePrompt,
      hasActivePrompt: !!activePrompt,
    }}>
      {children}
    </PromptQueueContext.Provider>
  );
}

export function usePromptQueue() {
  const ctx = useContext(PromptQueueContext);
  if (!ctx) throw new Error("usePromptQueue must be used within PromptQueueProvider");
  return ctx;
}
